// Single-active-tab gate built on the Web Locks API (navigator.locks).
//
// Why this exists:
//   The app should hold at most one MQTT connection per browser. If a user
//   opens a second tab, we don't want two sockets racing on the same
//   credentials — instead the second tab waits until the first one closes
//   (the browser releases the lock automatically on page unload).
//
// Browser support:
//   Web Locks is in every current evergreen browser. On unsupported
//   runtimes (notably SSR), the caller should fall through to its
//   no-lock behaviour rather than crashing.
//
// Why this isn't inline in MqttContext:
//   The state machine is timing-sensitive (acquired/blocked/released)
//   which makes it worth its own unit-tested module. The provider only
//   needs the two-promise interface, no Web Locks knowledge.

/** Minimal slice of the navigator.locks API used here — accepts a fake in
 *  tests. The real navigator.locks returns and accepts more, but we only
 *  use these. */
export interface LocksLike {
  request: (
    name: string,
    options: { signal?: AbortSignal },
    callback: (lock: unknown | null) => Promise<unknown>,
  ) => Promise<unknown>;
  /** Optional snapshot of who's holding which lock right now. */
  query?: () => Promise<{ held?: Array<{ name?: string }>; pending?: Array<{ name?: string }> }>;
}

export type TabLock = {
  /** Resolves the first time the lock is held by this tab. Never rejects. */
  acquired: Promise<void>;
  /** Resolves immediately if we can tell the lock is held by another tab
   *  right now. Used for fast 'blocked' UI without waiting for the queued
   *  request to resolve. May resolve to `false` on browsers without
   *  locks.query() — caller treats unknown as "not blocked". */
  initiallyBlocked: Promise<boolean>;
  /** Release the lock. Idempotent. Safe to call before acquired resolves
   *  (in which case the pending request is cancelled via the AbortSignal). */
  release: () => void;
};

/**
 * Acquire a named tab-scoped lock. If another tab holds the lock, the
 * returned `acquired` promise stays pending until that tab releases it
 * (typically by closing). `initiallyBlocked` resolves true if a peek at
 * navigator.locks.query() shows the lock is already held when we ask.
 */
export function acquireTabLock(locks: LocksLike, name: string): TabLock {
  const abort = new AbortController();
  let resolveAcquired!: () => void;
  let resolveRelease!: () => void;
  let released = false;

  const acquired = new Promise<void>((resolve) => {
    resolveAcquired = resolve;
  });
  const releaseSentinel = new Promise<void>((resolve) => {
    resolveRelease = resolve;
  });

  // IMPORTANT: query first, request second.
  //
  // navigator.locks.query() captures a snapshot of the current holders at
  // the time of the call. If we issued the request first, we'd add
  // ourselves to the holder set synchronously — the snapshot would then
  // include us, and `initiallyBlocked` would always be true. Querying
  // first means the snapshot reflects "is the lock held BEFORE we ask".
  const queryPromise: Promise<{ held?: Array<{ name?: string }> } | null> =
    locks.query ? locks.query().catch(() => null) : Promise.resolve(null);

  // Queue for the lock. The callback only runs once it's ours.
  locks
    .request(name, { signal: abort.signal }, async (lock) => {
      if (!lock || abort.signal.aborted) return;
      resolveAcquired();
      // Hold the lock until release() is called.
      await releaseSentinel;
    })
    .catch(() => {
      // AbortError or rejection — nothing to do, release() also resolves
      // releaseSentinel so any waiters move on.
    });

  const initiallyBlocked: Promise<boolean> = queryPromise.then((state) => {
    if (!state) return false;
    if (released) return false;
    return Boolean(state.held?.some((l) => l.name === name));
  });

  const release = () => {
    if (released) return;
    released = true;
    // Either resolves the inner promise (if we hold the lock) or aborts the
    // pending request (if we're still queued). Both are safe.
    resolveRelease();
    abort.abort();
  };

  return { acquired, initiallyBlocked, release };
}
